import React, { useEffect, useRef } from "react";
import L from "leaflet";

import "leaflet/dist/leaflet.css";
import styles from "./Map.module.css";

import { addLayerPOO } from "../layers/layerPointOfOrientation";
import { addLayerRoute } from "../layers/layerRoute";
import { overlayLayers } from "../layers/overlayLayerRegistry";


// marker icon for a point with a picture; built from DOM nodes, so names
// and URLs from the database are never parsed as HTML
function imageIcon(loc) {
  const root = document.createElement("div");
  root.className = styles.imageMarker;

  const img = document.createElement("img");
  img.src = loc.imageUrl;
  img.alt = "";
  img.className = styles.imageMarkerThumb;

  const label = document.createElement("span");
  label.textContent = loc.name;
  label.className = styles.imageMarkerLabel;

  root.append(img, label);

  return L.divIcon({
    html: root,
    className: "", // drop Leaflet's default white square
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

// marker icon showing a category's uploaded icon (SVG from NocoDB)
function categoryIcon(iconUrl) {
  const img = document.createElement("img");
  img.src = iconUrl;
  img.alt = "";
  img.className = styles.categoryMarker;

  return L.divIcon({
    html: img,
    className: "", // drop Leaflet's default white square
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

// "Sie sind hier" marker for a QR code location: pulsing dot + label,
// built from DOM nodes like the icons above
function youAreHereIcon(name) {
  const root = document.createElement("div");
  root.className = styles.youAreHere;

  const dot = document.createElement("span");
  dot.className = styles.youAreHereDot;

  const label = document.createElement("span");
  label.className = styles.youAreHereLabel;
  label.textContent = name ? `Sie sind hier: ${name}` : "Sie sind hier";

  root.append(dot, label);

  return L.divIcon({
    html: root,
    className: "", // drop Leaflet's default white square
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function MapView({
  locations = [],
  // zoom to the points when they change; off for the QR overview (all POIs),
  // which must not move the map away from the QR location
  autoFit = true,
  orientationPoints = [],
  routes = [],
  activeOverlays = {},
  renderPopup,
  getMarkerOptions,
  onSelectOrientationPoint,
  onSelectLocation,
  focus,
}) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const mapContainerRef = useRef(null);
  const roadsRef = useRef([]);
  const overlayLayerRefs = useRef({});
  const secondaryRoadsRef = useRef([]);
  const orientationLayerRef = useRef(null);
  const routeLayerRef = useRef(null);

  const userLocationLayerRef = useRef(null);
  const youAreHereRef = useRef(null);

  const hasRoutes = routes.length > 0;

  // init map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      crs: L.CRS.EPSG3857,
      zoomControl: true,
      attributionControl: false,
    }).setView([54.275, 13.7], 15);

    // for checking the zoom level in the console
    mapRef.current.on("zoomend", () => {
      console.log("Aktueller Zoom:", mapRef.current.getZoom());
    });

    mapContainerRef.current.style.backgroundColor = "#dff2fd";

    // panes, layers and z-indexes
    const panes = {
      // up
      markerPane: 850,
      countryNamePane: 800,
      poiPane: 750,
      riversPane: 650,
      cityNamePane: 550,
      // Themenrouten: above all background layers (roads, cycleway, forest, …)
      // but below every label — POI names are tooltips (Leaflet's tooltipPane,
      // 650) and POO names are markers (markerPane above)
      routing: 530,
      cycleway: 520,
      road_main: 510,
      road_secondary: 500,
      forestPane: 490,
      beachPane: 480,
    };

    Object.entries(panes).forEach(([name, zIndex]) => {
      if (!mapRef.current.getPane(name)) {
        mapRef.current.createPane(name);
      }

      mapRef.current.getPane(name).style.zIndex = zIndex;
    });

    // return
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // keep Leaflet in sync with the container size, e.g. when a module's list
  // appears next to the map and narrows it
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const observer = new ResizeObserver(() => mapRef.current?.invalidateSize());
    observer.observe(mapContainerRef.current);

    return () => observer.disconnect();
  }, []);

  // add orientation layer
  useEffect(() => {
    if (!mapRef.current) return;

    if (orientationLayerRef.current) {
      mapRef.current.removeLayer(orientationLayerRef.current);
    }

    if (!orientationPoints.length) return;

    // adpation of the zoom levels for the orientation layer
    orientationLayerRef.current = addLayerPOO(
      mapRef.current,
      orientationPoints,
      {
        minZoom: 13,
        maxZoom: 20,
        onSelect: onSelectOrientationPoint,
      },
    );

    return () => {
      if (orientationLayerRef.current) {
        mapRef.current?.removeLayer(orientationLayerRef.current);
      }
    };
  }, [orientationPoints, onSelectOrientationPoint]);

  // add route layer (Themenrouten)
  useEffect(() => {
    if (!mapRef.current) return;

    if (routeLayerRef.current) {
      mapRef.current.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }

    if (!routes.length) return;

    routeLayerRef.current = addLayerRoute(mapRef.current, routes);

    // zoom to the shown route(s). The side column (route steps) may have just
    // appeared and narrowed the map — re-read its size first, or the route's
    // far end ends up outside the visible area
    mapRef.current.invalidateSize();
    const bounds = routeLayerRef.current.getBounds();
    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      if (routeLayerRef.current) {
        mapRef.current?.removeLayer(routeLayerRef.current);
        routeLayerRef.current = null;
      }
    };
  }, [routes]);

  // openstreetmap beach
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/beach.geojson");
        const data = await response.json();
        L.geoJSON(data, {
          style: () => ({
            color: "#fcf5b8",
            fillColor: "#fcf5b8",
            weight: 0.5,
            fillOpacity: 1,
          }),
          pane: "beachPane",
        }).addTo(mapRef.current);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

    // openstreetmap labels, forest
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/forest.geojson");
        const data = await response.json();
        L.geoJSON(data, {
          style: () => ({
            color: "#fcf5b8",
            fillColor: "#92c5a0",
            weight: 0,
            fillOpacity: 1,
          }),
          pane: "forestPane",
        }).addTo(mapRef.current);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

  // openstreetmap label, scountries building for styling
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/building.json");
        const data = await response.json();
        L.geoJSON(data, {
          style: () => ({
            color: "#e3e3e3",
            fillColor: "#e3e3e3",
            weight: 0.5,
            fillOpacity: 1,
          }),
        }).addTo(mapRef.current);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

  // overlay layers (e.g. Zielgruppe -> Fahrrad-Routen), toggled generically
  // via activeOverlays + the overlayLayers registry — adding a new overlay
  // only means adding an entry to that registry, nothing changes here.
  useEffect(() => {
    if (!mapRef.current) return;

    let cancelled = false;

    Object.entries(overlayLayers).forEach(([key, loadLayer]) => {
      const isActive = !!activeOverlays[key];
      const existingLayer = overlayLayerRefs.current[key];

      if (isActive && !existingLayer) {
        loadLayer()
          .then((layer) => {
            if (cancelled || !mapRef.current) return;
            layer.addTo(mapRef.current);
            overlayLayerRefs.current[key] = layer;
          })
          .catch((err) => {
            console.error("Fehler beim Laden von GeoJSON:", err);
          });
      } else if (!isActive && existingLayer) {
        mapRef.current.removeLayer(existingLayer);
        delete overlayLayerRefs.current[key];
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeOverlays]);

  // main road
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/road.json");
        const data = await response.json();
        const roadLayer = L.geoJSON(data, {
          style: () => ({
            color: "#ffffff",
            weight: 3,
            fillOpacity: 1,
          }),
          pane: "road_main",
        });

        roadLayer.addTo(mapRef.current);
        roadsRef.current.push(roadLayer);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

  // openstreetmap labels, countries road 1 for styling
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/road_1.json");
        const data = await response.json();
        const roadLayer = L.geoJSON(data, {
          style: () => ({
            color: "rgb(255, 255, 255)",
            weight: 1,
            fillOpacity: 1,
          }),
          pane: "road_secondary",
        });

        roadLayer.addTo(mapRef.current);
        secondaryRoadsRef.current.push(roadLayer);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

  // GEOJSON LAYER
  useEffect(() => {
    if (!mapRef.current) return;

    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/countries.json");
        const data = await response.json();

        L.geoJSON(data, {
          style: () => ({
            color: "#5b9cd5",
            fillColor: "#b5dcd2",
            weight: 0.5,
            fillOpacity: 1,
          }),
        }).addTo(mapRef.current);
      } catch (err) {
        console.error("Fehler beim Laden von GeoJSON:", err);
      }
    };

    loadGeoJSON();
  }, []);

  // points of interest, markers
  useEffect(() => {
    if (!mapRef.current) return;

    // remove old markers
    markersRef.current.forEach((m) => mapRef.current.removeLayer(m));
    markersRef.current = [];

    if (!locations || locations.length === 0) return;

    const bounds = L.latLngBounds([]);

    locations.forEach((loc) => {
      const lat = Number(loc.latitude ?? loc.lat);
      const lng = Number(loc.longitude ?? loc.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // points with a picture (e.g. POIs along a Themenroute): round
      // thumbnail + name; click opens the detail panel
      if (loc.imageUrl) {
        const marker = L.marker([lat, lng], { icon: imageIcon(loc) })
          .addTo(mapRef.current)
          .on("click", () => onSelectLocation?.(loc));

        markersRef.current.push(marker);
        bounds.extend([lat, lng]);
        return;
      }

      // points with a category icon (Briefkasten, Restaurant, …): the icon
      // as marker, same label + popup as plain points
      if (loc.iconUrl) {
        const marker = L.marker([lat, lng], { icon: categoryIcon(loc.iconUrl) })
          .addTo(mapRef.current)
          .bindTooltip(loc.name, {
            permanent: true,
            direction: "right",
            offset: [14, 0],
            className: styles.mapLabel,
            interactive: true,
          })
          .bindPopup(`
            <div>
              <h3>${loc.name}</h3>
              <p>${loc.info || ""}</p>
            </div>
          `);

        markersRef.current.push(marker);
        bounds.extend([lat, lng]);
        return;
      }

      const markerOpts = getMarkerOptions
        ? getMarkerOptions(loc)
        : {
            radius: 0,
            fillColor: "#6a1111",
            fillOpacity: 0.6,
            color: "#000",
            weight: 0,
            pane: "markerPane",
          };

      const marker = L.circleMarker([lat, lng], markerOpts)
        .addTo(mapRef.current)
        .bindTooltip(loc.name, {
          permanent: true,
          direction: "right",
          offset: [10, -10],
          className: styles.mapLabel,
          interactive: true,
        })
        .bindPopup(`
          <div>
            <h3>${loc.name}</h3>
            <p>${loc.info || ""}</p>
          </div>
        `);

      markersRef.current.push(marker);
      bounds.extend([lat, lng]);
    });

    // auto zoom — unless a route is shown, then MapView zooms to the route
    if (bounds.isValid() && !hasRoutes && autoFit) {
      mapRef.current.setView(
        bounds.getCenter(),
        15.5
      );
    }
    // if (bounds.isValid()) {
    //   if (locations.length === 1) {
    //     mapRef.current.setView(bounds.getCenter(), 10);
    //   } else {
    //     mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    //   }
    // }


  }, [locations, renderPopup, getMarkerOptions, onSelectLocation, hasRoutes, autoFit]);

  // jump to a point selected in a module's result list, or to a QR code
  // location (focus.zoom = its zoom_stufe, focus.youAreHere = its name)
  useEffect(() => {
    if (!mapRef.current || !focus) return;

    const lat = Number(focus.latitude);
    const lng = Number(focus.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const zoom = Number(focus.zoom);
    mapRef.current.setView(
      [lat, lng],
      Number.isFinite(zoom) && zoom > 0 ? zoom : Math.max(mapRef.current.getZoom(), 16)
    );

    // the marker stays while the user browses; only a new QR focus replaces it
    if (focus.youAreHere !== undefined) {
      youAreHereRef.current?.remove();
      youAreHereRef.current = L.marker([lat, lng], {
        icon: youAreHereIcon(focus.youAreHere),
        interactive: false,
        zIndexOffset: 1000,
      }).addTo(mapRef.current);
    }
  }, [focus]);

  // adjust styles based on zoom level
  useEffect(() => {
    if (!mapRef.current) return;

    const updateStyles = () => {
      const zoom = mapRef.current.getZoom();

      const roadScale = Math.pow(1.55, zoom - 13);

      roadsRef.current.forEach((layer) => {
        layer.setStyle({
          weight: 3 * roadScale,
        });
      });

      secondaryRoadsRef.current.forEach((layer) => {
        layer.setStyle({
          weight: 1.5 * roadScale,
        });
      });

      // markersRef.current.forEach((marker) => {
      //   marker.setRadius(Math.max(4, 4 * roadScale));
      // });

      console.log("Zoom:", zoom, "Scale:", roadScale);
    };

    mapRef.current.on("zoomend", updateStyles);

    updateStyles();

    return () => {
      mapRef.current?.off("zoomend", updateStyles);
    };
  }, []);


  // Render the map container
  return (
    <div ref={mapContainerRef} style={{ height: "68vh", width: "100%" }} />
  );
}

export default MapView;
